import { AVAILABILITY_STATUS } from './availabilityStatus.js'
import { PRODUCT_CATEGORY }    from './productCategories.js'
import { getInsolationBucket } from '../utils/productInsolation.js'

/**
 * Color-mode options for the TiersView and ModelView legend / SVG coloring.
 *
 * - ROOMS:      apartments colored by number of rooms
 * - STATUS:     apartments colored by availability status
 * - INSOLATION: apartments colored by total direct sunlight hours (0–10 + null)
 * - COMPARE:    apartments colored by relative area within same rooms-count group (p0–p9 deciles)
 * - CATEGORY:   apartments colored by product_category
 */
export const COLOR_MODE = {
  ROOMS:      'rooms',
  STATUS:     'status',
  INSOLATION: 'insolation',
  COMPARE:    'compare',
  CATEGORY:   'category',
}

export const FALLBACK_COLOR_MODE = COLOR_MODE.ROOMS


/**
 * Registry of color-mode configurations.
 *
 * Each entry describes how a color mode extracts legend items from product data
 * and how to render swatches. Adding a new mode = one new entry here + CSS rules.
 *
 * Shape per entry:
 *   labelKey           i18n key for the Select dropdown option
 *   getLegendKey(p)    extracts the grouping value from a product.
 *                      null for modes that require external precomputation (COMPARE).
 *   buildItems(keys)   returns ordered array of { key, labelKey?, labelFn?, fill, stroke, hoverFill }
 *                      labelKey  → static i18n key
 *                      labelFn   → (t, plural, key) => string  (dynamic label)
 *   cssClassForKey(k)  SVG class name applied by useSvgProductInteraction
 *   filterId           catalog FILTER_IDS key (required — every colorMode must have a filter)
 *   toFilterValue(k)   converts a legend key to a catalog filter value
 */
export const COLOR_MODE_REGISTRY = {
  [COLOR_MODE.ROOMS]: {
    labelKey: 'color_modes.rooms',
    filterId: 'rooms',

    getLegendKey: (product) => product.rooms,

    toFilterValue: (legendKey) => [legendKey, legendKey],

    matchesFilterValue: (filterValue, legendKey) => (
      Array.isArray(filterValue)
      && filterValue.length === 2
      && Number(legendKey) >= Number(filterValue[0])
      && Number(legendKey) <= Number(filterValue[1])
    ),

    buildItems: (uniqueKeys) =>
      [...uniqueKeys]
        .sort((a, b) => a - b)
        .map(rooms => {
          const idx = rooms === 0 ? 0 : rooms <= 10 ? rooms : ((rooms - 1) % 10) + 1
          return {
            key:     rooms,
            labelFn: (t, plural, r) =>
              r === 0 ? t('tiers.rooms_service') : plural('tiers.rooms_count', r, { count: r }),
            fill:      `var(--bim-color-rooms-${idx}-fill)`,
            stroke:    `var(--bim-color-rooms-${idx})`,
            hoverFill: `var(--bim-color-rooms-${idx}-fill-hover)`,
          }
        }),

    cssClassForKey: (rooms) => `rooms-${rooms}`,
  },

  [COLOR_MODE.STATUS]: {
    labelKey: 'color_modes.status',
    filterId: 'status',

    getLegendKey: (product) => product.status,

    toFilterValue: (legendKey) => [legendKey],

    matchesFilterValue: (filterValue, legendKey) => (
      Array.isArray(filterValue)
      && filterValue.map(String).includes(String(legendKey))
    ),

    buildItems: (uniqueKeys) => {
      const order = [
        AVAILABILITY_STATUS.AVAILABLE,
        AVAILABILITY_STATUS.RESERVED,
        AVAILABILITY_STATUS.SOLD,
      ]
      return order
        .filter(s => uniqueKeys.has(s))
        .map(status => ({
          key:          status,
          labelKey:     `status.${status}`,
          fill:         `var(--bim-color-status-${status}-fill)`,
          stroke:       `var(--bim-color-status-${status})`,
          hoverFill:    `var(--bim-color-status-${status})`,
          hoverOpacity: 0.7,
        }))
    },

    cssClassForKey: (status) => `status-${status}`,
  },

  [COLOR_MODE.INSOLATION]: {
    labelKey: 'color_modes.insolation_hours',
    filterId: 'insolation',

    getLegendKey: (product) => getInsolationBucket(product.sun_light_ranges),

    toFilterValue: (legendKey) => [String(legendKey)],

    matchesFilterValue: (filterValue, legendKey) => (
      Array.isArray(filterValue)
      && filterValue.map(String).includes(String(legendKey))
    ),

    buildItems: (uniqueKeys) => {
      // Order: numeric buckets 0–10 ascending, 'null' last
      const sorted = [...uniqueKeys].sort((a, b) => {
        if (a === 'null') return 1
        if (b === 'null') return -1
        return Number(a) - Number(b)
      })
      return sorted.map(k => ({
        key:      k,
        labelKey: k === 'null' ? 'tiers.insolation_null' : `tiers.insolation_${k}h`,
        fill:     `var(--bim-color-insolation-${k}-fill)`,
        stroke:   `var(--bim-color-insolation-${k})`,
        hoverFill: `var(--bim-color-insolation-${k}-fill-hover)`,
      }))
    },

    cssClassForKey: (k) => `insolation-${k}`,
  },

  [COLOR_MODE.COMPARE]: {
    labelKey: 'color_modes.compare',
    filterId: 'compare_area',

    // null — key is determined externally via buildProductAreaDeciles Map<uuid, bucket>
    // passed as colorModeDeciles to useSvgProductInteraction
    getLegendKey: null,

    toFilterValue: (legendKey) => [legendKey],

    matchesFilterValue: (filterValue, legendKey) => (
      Array.isArray(filterValue)
      && filterValue.map(String).includes(String(legendKey))
    ),

    buildItems: (_uniqueKeys) =>
      // All 10 buckets always shown — comparison scale is only meaningful when complete
      ['p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8', 'p9'].map(k => ({
        key:      k,
        labelKey: `tiers.compare_${k}`,
        fill:     `var(--bim-color-compare-${k}-fill)`,
        stroke:   `var(--bim-color-compare-${k})`,
        hoverFill: `var(--bim-color-compare-${k}-fill-hover)`,
      })),

    cssClassForKey: (k) => `compare-${k}`,
  },

  [COLOR_MODE.CATEGORY]: {
    labelKey: 'color_modes.category',
    filterId: 'category',

    getLegendKey: (product) => product.product_category ?? null,

    toFilterValue: (legendKey) => [legendKey],

    matchesFilterValue: (filterValue, legendKey) => (
      Array.isArray(filterValue)
      && filterValue.map(String).includes(String(legendKey))
    ),

    buildItems: (uniqueKeys) => {
      // Preserve canonical enum order, only include categories present in the dataset
      const order = Object.values(PRODUCT_CATEGORY)
      return order
        .filter(k => uniqueKeys.has(k))
        .map(k => ({
          key:      k,
          labelKey: `product_category_strings.${k}`,
          fill:     `var(--bim-color-category-${k.replaceAll('_', '-')}-fill)`,
          stroke:   `var(--bim-color-category-${k.replaceAll('_', '-')})`,
          hoverFill: `var(--bim-color-category-${k.replaceAll('_', '-')}-fill-hover)`,
        }))
    },

    cssClassForKey: (k) => `category-${k}`,
  },
}
